import { Info, Sparkles } from "lucide-react";
import { Link } from "react-router";
import DOMPurify from "dompurify";
import { useTranslation, Trans } from "react-i18next";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../../../../shared/ui/table";
import Button from "../../../../../shared/ui/button/Button";
import { AiBadge, Surface } from "../../../../../shared/ui/primitives";


import { SessionAnalysis, TriggeredAlert } from "../../../domain/Session";
import { LockedState } from "../../../../ai_insights";
import { AlertTriangle } from "lucide-react";


interface SessionAIAnalysisFormProps {
  analysis?: SessionAnalysis | null;
  handleAnalyze: () => void;
  analyzing?: boolean;
  analysisSkippedReason?: string;
  analyzeError?: string | null;
  /** AI Insights tool not adopted: existing analysis stays readable, re-running does not. */
  locked?: boolean;
}

export default function SessionViewAIAnalysisForm({
  analysis,
  analyzing = false,
  handleAnalyze,
  analysisSkippedReason,
  analyzeError,
  locked = false,
}: SessionAIAnalysisFormProps) {
  const { t } = useTranslation();

  return (
    <Surface aiGenerated padding="lg" radius="2xl">
      <div className="mb-8 flex w-full flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-content-primary">
            {t("session.aiInsightsTitle")}
          </h2>
          {/* States the provenance in words. The spectrum rule above says the
              same thing in colour, and colour alone would not say it at all. */}
          <AiBadge label={t("aiInsights.generatedBadge")} />
        </span>

        {!locked && (
          <Button
            onClick={handleAnalyze}
            loading={analyzing}
            startIcon={<Sparkles className="size-4" />}
          >
            {analyzing ? t("session.analyzing") : t("session.runAnalysis")}
          </Button>
        )}
      </div>

      {locked && (
        <div className="mb-8">
          <LockedState title={t("aiInsights.locked.sessionAnalysis")} compact />
        </div>
      )}

      <div className="space-y-8">
        {/* AI Frame */}
        {/* The hand-rolled purple/blue/cyan gradient border is the shared
            ai-spectrum-border utility now, so it stays in step with every other
            AI-provenance marker in the product. */}
        <div className="ai-spectrum-border mb-8 rounded-xl">
          <div className="flex items-start gap-4 rounded-xl p-5">
            <Info aria-hidden="true" className="mt-1 size-4 shrink-0 text-accent-text" />
            <p className="text-sm font-medium leading-relaxed text-content-secondary">
              <Trans
                i18nKey="session.aiInsightsDesc"
                components={{ 1: <strong className="text-accent-text" /> }}
              />
            </p>
          </div>
        </div>


        {/* Consent error notice */}
        {analyzeError && (
          <div className="flex items-start gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50/60 dark:bg-red-900/10">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-300 leading-relaxed">
              {analyzeError}
            </p>
          </div>
        )}

        {/* Analysis skipped notice */}
        {analysisSkippedReason === "notes_too_short" && (
          <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50/60 dark:bg-amber-900/10">
            <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
              {t("session.analysisSkippedNotesTooShort")}
            </p>
          </div>
        )}

        {/* Finished on purpose, not for want of trying */}
        {analysisSkippedReason === "analysis_declined" && !analysis && (
          <div className="flex items-start gap-3 p-4 rounded-xl border border-border-subtle bg-surface-sunken">
            <Info className="w-4 h-4 text-content-tertiary mt-0.5 flex-shrink-0" />
            <p className="text-sm text-content-secondary leading-relaxed">
              {t("session.analysisDeclined")}
            </p>
          </div>
        )}

        {/* Si hay resultados */}
        {analysis && (
          <>
            {/* Summary */}
            <AISummary
              main_topic={analysis.main_topic}
              emotional_status={analysis.emotional_status}
              relevant_behaviors={analysis.relevant_behaviors}
              therapeutic_advances={analysis.therapeutic_advances}
              risks={analysis.risks}
              agreements={analysis.agreements}
            />

            {/* Tables */}
            <RelatedSessionsTable data={analysis.related_sessions || []} />
            <AcademicArticlesTable data={analysis.academic_articles || []} />
            <RecommendationsTable data={analysis.recommendations || []} />
          </>
        )}
      </div>
    </Surface>
  );
}

/* -------------------------
   🔹 Summary
-------------------------- */
export function AISummary({
  main_topic,
  emotional_status,
  relevant_behaviors,
  therapeutic_advances,
  risks,
  agreements,
  showTitle = true,
}: {
  main_topic?: string;
  emotional_status?: string;
  relevant_behaviors?: string;
  therapeutic_advances?: string;
  risks?: string;
  agreements?: string;
  showTitle?: boolean;
}) {
  const { t } = useTranslation();
  const hasAnyField =
    main_topic ||
    emotional_status ||
    relevant_behaviors ||
    therapeutic_advances ||
    risks ||
    agreements;

  if (!hasAnyField) return null;

  return (
    <div className="group rounded-2xl border border-border-subtle bg-white/50 dark:bg-white/[0.02] backdrop-blur-sm p-6 mb-6 shadow-theme-sm transition-all hover:shadow-theme-md">
      {showTitle && (
        <h3 className="mb-4 text-lg font-semibold text-content-primary">
          {t("session.detailedOverview")}
        </h3>
      )}
      <div className="space-y-4 text-sm text-gray-700 dark:text-gray-400">
        {main_topic && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.mainTopic")}</h4>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(main_topic) }} />
          </div>
        )}

        {emotional_status && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.emotionalStatus")}</h4>
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(emotional_status),
              }}
            />
          </div>
        )}

        {relevant_behaviors && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.relevantBehaviors")}</h4>
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(relevant_behaviors),
              }}
            />
          </div>
        )}

        {therapeutic_advances && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.therapeuticAdvances")}</h4>
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(therapeutic_advances),
              }}
            />
          </div>
        )}

        {risks && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.detectedRisks")}</h4>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(risks) }} />
          </div>
        )}

        <div>
          <h4 className="font-medium text-content-primary">{t("session.agreements.title")}</h4>
          {agreements
            ? <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(agreements) }} />
            : <p className="text-sm text-content-tertiary italic">{t("session.agreements.noneInSession")}</p>
          }
        </div>
      </div>
    </div>
  );
}

/* -------------------------
   🔹 Related Sessions Table
-------------------------- */
import { RelatedSession } from "../../../domain/Session";
import { formatDate } from "../../../../../shared/utils/date";
// ...
function RelatedSessionsTable({ data }: { data: RelatedSession[] }) {
  const { t, i18n } = useTranslation();
  if (!data.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 className="mb-4 text-lg font-semibold text-content-primary">
        {t("session.relatedSessionsTitle")}
      </h3>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <Table>
          <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
            <TableRow>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.date")}</TableCell>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.title")}</TableCell>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.similarity")}</TableCell>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.therapyType")}</TableCell>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.patient")}</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
            {data.map((item, i) => (
              <TableRow key={i}>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                  <Link
                    to={`/session/${item.session_id}`}
                    className="text-accent-text hover:underline"
                  >
                    {item.date
                      ? formatDate(item.date, i18n.language)
                      : "—"}
                  </Link>
                </TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">{item.title}</TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">{new Intl.NumberFormat("es-ES", { style: "percent", minimumFractionDigits: 2 }).format(item.similarity)}</TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">{formatTherapyTypes(t, item.therapy_type)}</TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                  <div>
                    <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">{item.patient_name}</span>
                    <span className="block text-gray-500 text-theme-xs dark:text-gray-400">{item.patient_last_name}</span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* -------------------------
   🔹 Academic Articles Table
-------------------------- */
import { AcademicArticle } from "../../../domain/Session";
// ...
function AcademicArticlesTable({ data }: { data: AcademicArticle[] }) {
  const { t } = useTranslation();
  if (!data.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 className="mb-4 text-lg font-semibold text-content-primary">
        {t("session.academicArticlesTitle")}
      </h3>

      <ul className="space-y-4">
        {data.map((recomendation) => (
          <li
            key={recomendation.id}
            className="p-4 border border-gray-200 rounded-xl dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/20 transition"
          >
            <h4 className="font-medium text-content-primary">
              {t("session.topic")}: {recomendation.title}
            </h4>
            <p className="text-sm text-content-secondary mt-1">
              {t("session.author")}: {recomendation.author}
            </p>

            <Link
              to={recomendation.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-brand-500 hover:text-brand-600 text-sm"
            >
              {t("session.goToRecommendation")}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------
   🔹 Recommendations Table
-------------------------- */
import { Recommendation } from "../../../domain/Session";
import { formatTherapyTypes } from "../../../../../shared/domain/therapyTypes";
// ...
function RecommendationsTable({ data }: { data: Recommendation[] }) {
  const { t } = useTranslation();
  if (!data.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 className="mb-4 text-lg font-semibold text-content-primary">
        {t("session.recommendationsTitle")}
      </h3>

      <ul className="space-y-4">
        {data.map((recomendation) => (
          <li
            key={recomendation.id}
            className="p-4 border border-gray-200 rounded-xl dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/20 transition"
          >
            <h4 className="font-medium text-content-primary">
              {recomendation.title}
            </h4>
            <p className="text-sm text-content-secondary mt-1">
              {recomendation.description}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------
   🔹 Triggered Alerts List
-------------------------- */
export function TriggeredAlertsList({ data }: { data: TriggeredAlert[] }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;

  const getAlertStyle = (criticality: string) => {
    const lower = criticality.toLowerCase();
    if (lower.includes("low") || lower.includes("baja")) {
      return {
        border: "border-status-info/25",
        bg: "bg-status-info-tint",
        text: "text-status-info-on-tint",
        badge: "bg-status-info-tint text-status-info-on-tint",
        icon: "text-status-info",
      };
    }
    if (lower.includes("medium") || lower.includes("media") || lower.includes("yellow")) {
      return {
        border: "border-status-warning/25",
        bg: "bg-status-warning-tint",
        text: "text-status-warning-on-tint",
        badge: "bg-status-warning-tint text-status-warning-on-tint",
        icon: "text-status-warning",
      };
    }
    // High and critical.
    return {
      border: "border-status-critical/25",
      bg: "bg-status-critical-tint",
      text: "text-status-critical-on-tint",
      badge: "bg-status-critical-tint text-status-critical-on-tint",
      icon: "text-status-critical",
    };
  };

  return (
    <div className="mb-6 rounded-xl border border-border-subtle bg-surface-raised p-6">
      <h3 className="mb-4 flex items-center text-lg font-semibold text-content-primary">
        <AlertTriangle aria-hidden="true" className="mr-2 size-5 text-status-critical" />
        {t("session.detectedAlerts")}
      </h3>
      <ul className="grid grid-cols-1 gap-4">
        {data.map((alert) => {
          const style = getAlertStyle(alert.criticality);
          const normalized = alert.criticality.toLowerCase().includes("low") || alert.criticality.toLowerCase().includes("baja") ? "low" :
            alert.criticality.toLowerCase().includes("medium") || alert.criticality.toLowerCase().includes("media") ? "medium" :
              alert.criticality.toLowerCase().includes("critical") || alert.criticality.toLowerCase().includes("crítica") ? "critical" : "low";

          return (
            <li key={alert.alert_id} className={`p-4 rounded-xl shadow-sm border transition-all hover:shadow-md ${style.border} ${style.bg} backdrop-blur-md`}>
              <div className="flex justify-between items-start mb-2">
                <span className={`font-semibold ${style.text}`}>{alert.name}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase ${style.badge}`}>
                  {t(`clinicalAlerts.modal.criticality.${normalized}`)}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-content-secondary">{alert.context}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
